import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { CompanyAction, AdminUserAction } from '../../actions';
import { langSelectValue, langSelectOptions } from '../../lib/preLoginLang';
import { RayForm } from '../../components/common';
import { EditSaveCancelBar } from '../../components/common/Buttons';
import CustomFieldValues from '../../components/customfield/CustomFieldValues';
import { AdminFunctions, AdminTypes, AdminPosition } from '../../config/RayCodes';
import { Translate as t } from '../../config/Translate';
import { getCallerTier, isFieldEditableByTier } from '../../lib/userPermissionGuard';
import { ORG_HQ } from '../../config/appConstants';
import { userFieldLabel, dealerTypeOptions } from './userFormFields';
import { resolveStoredUserLangCode, resolveStoredMailLangCode } from '../../config/currentLang';
import { localizeUserFormErrors } from '../../lib/userFormErrors';
import { useBatchEdit } from '../../hooks/useBatchEdit';

// 서버 값(user) -> 편집용 draft(권한 체크박스 7개는 즉시 저장이라 없다). 키 = RayForm 칸 name = 서버로 보내는 키.
const toDraft = (user) => ({
    name: user?.name,
    companyId: user?.companyId,
    firstName: user?.firstName,
    lastName: user?.lastName,
    email: user?.email,
    hp: user?.hp,
    tel: user?.tel,
    rguardSettingLanguage: langSelectValue(resolveStoredUserLangCode(user) || ''),
    rguardSettingEmailReportLanguage: resolveStoredMailLangCode(user),
    adminType: user?.adminType,
    function: user?.function,
    position: user?.position,
    csPortalLink: user?.csPortalLink,
    dealerType: user?.dealerType ?? 'internal',
});

const AdminUserEditInfo = ({ authuser, user, companyList, canedit, UpdateUserInfo, editable, apierrors, cancelcallback = null }) => {

    // IMP-468 (#507): 본 화면에는 서버 필드 에러를 받을 통로가 없어, 이메일을 중복 값으로 바꿔도
    // 어느 필드가 문제인지 드러나지 않았다. 생성 화면(AdminUserAdd)과 같은 경로로 연결한다.
    const [companies, setCompanies] = useState([]);
    const [guardeditable, setGuardEditable] = useState(false);
    const [adminTypeList, setAdminTypeList] = useState(AdminTypes);
    const { draft, editing, saving, errors: formErrors, formKey, onChange, onSave, onCancel } = useBatchEdit({
        source: user,
        toDraft,
        save: (changed) => UpdateUserInfo(user.adminid, changed),
        editable: !!editable,
        entry: 'header',
        onClose: cancelcallback,
    });
    const errors = useMemo(
        () => [...formErrors, ...localizeUserFormErrors(apierrors)],
        [formErrors, apierrors]
    );

    // SEC-004 / SEC-007 R2 / BUG-116(R3) — backend `resolveCallerPrivilege` 와 동일 tier
    // 정책을 FE 분기로 미러. silent skip UX 차단(disabled + 자물쇠).
    //   tier='hq'      → orgScoped + hqOnly 모두 편집 가능
    //   tier='sameOrg' → orgScoped + companyId 편집 가능 (companyId 는 sub-tree 안 이동만 — BUG-116)
    //   tier='none'    → 권한 필드 전부 차단
    // BE 가 fail-closed SSoT — FE 우회해도 BE sanitizeUpdateData 가 sub-tree value 검증.
    //
    // BUG-116 / SEC-007 R3 — '같은 organization' 안내 SSoT 정합. companyList 는 redux
    // state.CompanyReducer.companies 에서 BE `/company` getPermList 필터를 거친 caller
    // sub-tree(자신 + 자손) 회사 목록이므로, 그 안의 user 는 sameOrg.
    const accessibleCompanyIds = useMemo(
        () => (Array.isArray(companyList) ? companyList.map(c => c.companyId) : []),
        [companyList]
    );
    const callerTier = getCallerTier(authuser, user, accessibleCompanyIds);
    // 필드별 편집 가능 여부 — tier × 등급 분기. callerTier 1회 계산 후 재사용.
    const permFieldEditable = (fieldName) =>
        guardeditable && isFieldEditableByTier(fieldName, callerTier);
    // 체크박스 7개는 즉시 저장이라 permFieldEditable 그대로, 나머지 칸은 편집 중에만 연다.
    const permFieldEditing = (fieldName) => permFieldEditable(fieldName) && editing;
    const lockBadgeFor = (fieldName) =>
        isFieldEditableByTier(fieldName, callerTier) ? '' : ' 🔒';

    useEffect(() => {
        //GetCompany();
    }, []);

    useEffect(() => {
        if (editable) {
            if (authuser.adminType != "U" || (authuser.companyInfo && authuser.companyInfo.org == ORG_HQ)) {
                setGuardEditable(true);
            }
        } else {
            setGuardEditable(false);
        }
    }, [editable]);

    useEffect(() => {
        if (guardeditable) {
            if (authuser.companyInfo && authuser.companyInfo.org == ORG_HQ) {
                setAdminTypeList(AdminTypes);
            } else if (authuser.adminType == "M") {
                setAdminTypeList(AdminTypes.filter(x => x.code != "S"));
            } else if (authuser.adminType == "U") {
                setAdminTypeList(AdminTypes.filter(x => x.code == "U"));
            } else if (authuser.adminType == "S") {
                setAdminTypeList(AdminTypes);
            }
        } else {
            setAdminTypeList(AdminTypes);
        }
    }, [guardeditable]);

    useEffect(() => {
        if (companyList.length > 0)
            setCompanySelect();
    }, [companyList]);

    const setCompanySelect = () => {
        const cList = [];
        companyList.map((c) => cList.push({ code: c.companyId, title: c.name }));
        setCompanies(cList);
    }

    const storedLang = draft.rguardSettingLanguage;
    const mailLang = draft.rguardSettingEmailReportLanguage;
    const fieldEditing = !!editable && editing;
    const guardEditing = guardeditable && editing;
    // 체크박스 즉시 저장 - 편집 중 draft 와 무관하게 그 칸 값만 보낸다.
    const immediate = { action: UpdateUserInfo };

    const form_controls = [
        {
            title: `${t('user.title')} ${t('common.label.information')}`, items: [
                { type: "input", name: "name", label: t('common.field.name'), val: draft.name, isEditable: fieldEditing },
                // BUG-116(R3) — companyId 는 sameOrg 에서도 편집 가능 (sub-tree 안 이동만).
                // list 옵션이 redux companies(sub-tree)로 자동 제한 + BE sanitizeUpdateData value 검증으로 cross-sub-tree IDOR 방어.
                { type: "select2", name: "companyId", label: `${t('common.label.company')}${lockBadgeFor('companyId')}`, val: draft.companyId, list: companies, required: true, isEditable: permFieldEditing('companyId'), onlyOne: true },
                { type: "input", name: "firstName", label: t('common.field.firstName'), val: draft.firstName, isEditable: fieldEditing },
                { type: "input", name: "lastName", label: t('common.field.lastName'), val: draft.lastName, isEditable: fieldEditing },
                { type: "email", name: "email", label: t('common.field.email'), val: draft.email, required: true, isEditable: fieldEditing },
                { type: "tel", name: "hp", label: t('common.field.mobile'), val: draft.hp, isEditable: fieldEditing },
                { type: "tel", name: "tel", label: t('common.field.tel'), val: draft.tel, isEditable: fieldEditing },
                // IMP-475 P2c — 내 설정과 같은 두 필드. language 는 옛 계정 폴백으로만 남는다.
                { type: "select", name: "rguardSettingLanguage", label: userFieldLabel('rguardSettingLanguage'), val: storedLang, list: langSelectOptions(storedLang), isEditable: fieldEditing },
                { type: "select", name: "rguardSettingEmailReportLanguage", label: userFieldLabel('rguardSettingEmailReportLanguage'), val: mailLang, list: langSelectOptions(mailLang), isEditable: fieldEditing }
            ]
        },
        {
            title: `RAYGuard ${t('common.label.settings')}`, items: [
                // SEC-004 / SEC-007 R2 — orgScoped 권한 필드. caller=Super Admin AND (HQ \| same-org).
                // BE 가 fail-closed SSoT, FE 는 silent skip UX 차단(disabled + 자물쇠).
                { type: "select", name: "adminType", label: `${userFieldLabel('adminType')}${lockBadgeFor('adminType')}`, val: draft.adminType, list: adminTypeList, required: true, isEditable: permFieldEditing('adminType') },
                { type: "select", name: "function", label: userFieldLabel('function'), val: draft.function, list: AdminFunctions, required: true, isEditable: guardEditing },
                { type: "select", name: "position", label: t('common.field.position'), val: draft.position, list: AdminPosition, required: true, isEditable: guardEditing },
                { type: "input", name: "csPortalLink", label: userFieldLabel('csPortalLink'), size: 12, val: draft.csPortalLink, isEditable: guardEditing },
                { type: "checkbox", name: "isUse", ...immediate, label: `${userFieldLabel('isUse')}${lockBadgeFor('isUse')}`, val: user.isUse, isEditable: permFieldEditable('isUse') },
                { type: "checkbox", name: "permAdminAccess", ...immediate, label: `${userFieldLabel('permAdminAccess')}${lockBadgeFor('permAdminAccess')}`, size: 12, val: user.permAdminAccess, isEditable: permFieldEditable('permAdminAccess') },
                { type: "checkbox", name: "isEmail", ...immediate, label: userFieldLabel('isEmail'), val: user?.isEmail, isEditable: guardeditable},
                { type: "checkbox", name: "isTypeChange", ...immediate, label: `${userFieldLabel('isTypeChange')}${lockBadgeFor('isTypeChange')}`, val: user?.isTypeChange, isEditable: permFieldEditable('isTypeChange')},
                { type: "checkbox", name: "isCreateLicense", ...immediate, label: `${userFieldLabel('isCreateLicense')}${lockBadgeFor('isCreateLicense')}`, val: user?.isCreateLicense, isEditable: permFieldEditable('isCreateLicense')},
                { type: "checkbox", name: "isLicenseDownload", ...immediate, label: `${userFieldLabel('isLicenseDownload')}${lockBadgeFor('isLicenseDownload')}`, val: user?.isLicenseDownload, isEditable: permFieldEditable('isLicenseDownload')},
                { type: "checkbox", name: "isSetWarranty", ...immediate, label: `${userFieldLabel('isSetWarranty')}${lockBadgeFor('isSetWarranty')}`, val: user?.isSetWarranty, isEditable: permFieldEditable('isSetWarranty')},
                // FEAT-025 SPEC-RG-03 — 딜러 유형. SEC-007 R2 orgScoped tier → Subsidiary Super Admin 도 편집 가능.
                {
                    type: "select",
                    name: "dealerType",
                    label: `${userFieldLabel('dealerType')}${lockBadgeFor('dealerType')}`,
                    val: draft.dealerType,
                    list: dealerTypeOptions(),
                    isEditable: permFieldEditing('dealerType'),
                },
            ]
        },
    ]

    const UpdateExtra = (code, value) => {
        UpdateUserInfo(user.adminid, {
            customfieldvals: { ...user.customfieldvals, [code]: value }
        });
    }

    return (
        <>
            <div className="section mt-2 mb-2 text-center">
                <div className="avatar-section">
                    {user.avatar && <img src={user.avatar} alt="avatar" className="imaged w100 rounded" />}
                    {!user.avatar && <img src="/assets/img/avatar_default_none.png" alt="avatar" className="imaged w100 rounded" />}
                </div>
            </div>
            {/* SEC-004 / SEC-007 R2 / BUG-116(R3) — caller tier 별 안내 메시지 분기.
                - tier='none'    : 모든 권한 필드 read-only 안내.
                - tier='sameOrg' : companyId 이동은 sub-tree(자기 조직 + 산하) 안에서만 가능 안내.
                - tier='hq'      : 안내 미표시. */}
            {callerTier === 'none' && (
                <div className="section mt-1 mb-2">
                    <div className="alert alert-warning py-2 small mb-0">
                        🔒 {t('user.message.permissionFieldsReadOnly')}
                    </div>
                </div>
            )}
            {callerTier === 'sameOrg' && (
                <div className="section mt-1 mb-2">
                    <div className="alert alert-info py-2 small mb-0">
                        🔒 {t('user.message.userMoveRestricted')}
                    </div>
                </div>
            )}
            <div className="row">
                <div className="col-md-8">
                    <RayForm
                        controls={form_controls}
                        key={formKey}
                        handleChangeData={onChange}
                        showAllFields={true}
                        errors={errors}
                        actionkey={user.adminid} />
                    <div className="ps-1 pe-1 mb-2">
                        <EditSaveCancelBar canEdit={!!editable} editing={editing} onSave={() => onSave(form_controls)} onCancel={onCancel} saving={saving} />
                    </div>
                </div>
                <div className="col-md-4 p-2">
                    <CustomFieldValues model={"USER"} update={UpdateExtra} data={user.customfieldvals || {}} />
                </div>
            </div>
        </>
    )
}

const mapState = (state) => {
    const companyList = state.CompanyReducer.companies;
    const authuser = state.AuthReducer.user;
    const apierrors = state.AlertReducer.errors;
    return { authuser, companyList, apierrors };
}

const mapDispatch = dispatch => ({
    UpdateUserInfo: (adminid, data) => dispatch(AdminUserAction.UpdateUserInfo(adminid, data)),
    //GetCompany: () => dispatch(CompanyAction.GetCompany()),
})
export default connect(mapState, mapDispatch)(AdminUserEditInfo);